import type {
  AgentCodeTestConfigChange,
  AgentCodeTestConfigFields,
  AgentCodeTestDraftPr,
  AgentTestOperation,
} from '../../../../types';
import { isAgentCodeTestCreateChange } from '../../../../types';
import { isAgentTestOperation } from '../applyTestConfigPlanShared/sharedUtils';

// Worst case nesting we may need to peel before reaching the plan dict:
// 1 stringified-JSON layer + up to 2 `{ isError, result }` envelopes
// (the session infra can double-wrap) = 3, plus 1 for safety margin.
const MAX_RESULT_UNWRAP_DEPTH = 4;

type ParsedCodeTestPlanResult = {
  operation: AgentTestOperation;
  changes: AgentCodeTestConfigChange[];
  draftPr: AgentCodeTestDraftPr | null;
  failures: unknown[];
};

const parseDraftPr = (value: unknown): AgentCodeTestDraftPr | null => {
  if (!value || typeof value !== 'object') return null;
  const draft = value as Record<string, unknown>;
  const link = draft.link;
  const number = draft.number;
  const branch = draft.branch;
  const codeProjectId = draft.code_project_id ?? draft.codeProjectId;
  const envId = draft.env_id ?? draft.envId;
  const title = draft.title;
  if (
    typeof link !== 'string' ||
    typeof number !== 'number' ||
    typeof branch !== 'string' ||
    typeof codeProjectId !== 'string' ||
    typeof envId !== 'string' ||
    typeof title !== 'string'
  ) {
    return null;
  }
  const toNumberOrNull = (value: unknown): number | null =>
    typeof value === 'number' ? value : null;

  return {
    link,
    number,
    branch,
    codeProjectId,
    envId,
    title,
    additions: toNumberOrNull(draft.additions),
    deletions: toNumberOrNull(draft.deletions),
    changedFiles: toNumberOrNull(draft.changed_files ?? draft.changedFiles),
  };
};

export const parseCodeTestPlanResult = (
  raw: unknown,
): ParsedCodeTestPlanResult | null => {
  // The tool result can arrive in different shapes depending on the agent
  // transport: a stringified JSON (legacy), the plan dict directly,
  // or wrapped one or more times in an { isError, result } envelope (session
  // infra double-wraps it). Unwrap/parse until we reach the plan dict.
  let parsed: unknown = raw;
  for (let depth = 0; depth < MAX_RESULT_UNWRAP_DEPTH; depth += 1) {
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return null;
      }
      continue;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    const candidate = parsed as Record<string, unknown>;
    if (isAgentTestOperation(candidate.operation)) break;
    if ('result' in candidate) {
      parsed = candidate.result;
      continue;
    }
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  const operation = isAgentTestOperation(obj.operation) ? obj.operation : null;
  if (!operation) return null;
  const changes = Array.isArray(obj.changes)
    ? (obj.changes as AgentCodeTestConfigChange[])
    : [];
  return {
    operation,
    changes,
    draftPr: parseDraftPr(obj.pr ?? obj.draft_pr ?? obj.draftPr),
    failures: Array.isArray(obj.failures) ? obj.failures : [],
  };
};

export const formatCodeTestConfigFieldsSummary = (
  fields: AgentCodeTestConfigFields,
): string => {
  const parts: string[] = [];

  if (fields.severity)
    parts.push(
      `severity: ${fields.severity.current ?? 'none'} \u2192 ${
        fields.severity.new ?? 'none'
      }`,
    );
  if (fields.description) parts.push('description');
  if (fields.tags)
    parts.push(
      `tags: ${fields.tags.current?.length ?? 0} \u2192 ${
        fields.tags.new?.length ?? 0
      }`,
    );
  if (fields.owners)
    parts.push(
      `owners: ${fields.owners.current?.length ?? 0} \u2192 ${
        fields.owners.new?.length ?? 0
      }`,
    );
  if (fields.params) {
    const paramKeys = Object.keys(fields.params);
    if (paramKeys.length) parts.push(`params: ${paramKeys.join(', ')}`);
  }

  return parts.join(', ') || 'no changes';
};

export const formatCodeTestChangeSummary = (
  change: AgentCodeTestConfigChange,
  operation: AgentTestOperation,
): string => {
  if (operation === 'delete') {
    return 'delete test';
  }
  if (isAgentCodeTestCreateChange(change)) {
    const configSummary = formatCodeTestConfigFieldsSummary(change);
    return `Create ${change.testType} test — ${configSummary}`;
  }
  return formatCodeTestConfigFieldsSummary(change);
};

export const formatCodeTestChangeLabel = (
  change: AgentCodeTestConfigChange,
): string => {
  if (isAgentCodeTestCreateChange(change)) {
    return change.column
      ? `${change.resourceName}.${change.column}`
      : change.resourceName;
  }
  return change.testName || change.testId;
};
